import { observer } from 'mobx-react-lite';

import { Icons } from '@/assets/icons/icons';
import { Attachments } from '@/components/blocks/Message/Attachments';
import { XStack } from '@/components/ui';
import { IconButton } from '@/components/ui/IconButton';
import { Markdown } from '@/components/ui/smoothdown';
import { composerEditorOf } from '@/features/mention';
import { WikiUserAvatar } from '@/features/people-wiki/components/user-avatar';
import { cn } from '@/lib/utils';

import { teamChatMembersOf } from './team-chat-members.store';
import { insertMemberMention } from './team-chat-mention-source';
import { postBodyOf, type TeamChatAuthor } from './team-chat-messages';

import type { Message } from '@littlebird/sdk';

type TeamChatPostProps = {
  readonly threadId: string;
  readonly message: Message;
  readonly messageIndex: number;
  readonly author: TeamChatAuthor;
  readonly isOwn: boolean;
  readonly isChatOwner: boolean;
  readonly className?: string;
};

export const TeamChatPost = observer(function TeamChatPost({
  threadId,
  message,
  messageIndex,
  author,
  isOwn,
  isChatOwner,
  className,
}: TeamChatPostProps) {
  const body = postBodyOf(message.body, author.authorName);
  // The composer's draft id is the chat's id, and its members are who it can mention
  const member = teamChatMembersOf(threadId).find(
    (each) => each.userId === author.authorId
  );
  const reply = () => {
    const editor = composerEditorOf(threadId);
    if (editor && member) insertMemberMention(editor, member);
  };
  return (
    <div
      data-message-index={messageIndex}
      data-message-id={message.id}
      data-role={isOwn ? 'user' : 'teammate'}
      role="article"
      aria-label={isOwn ? 'Your post' : `Post from ${author.authorName}`}
      id={`message-${messageIndex}`}
      className={cn(
        'relative mb-8 flex w-full flex-col gap-1.5',
        isOwn ? 'items-end' : 'items-start',
        className
      )}
    >
      {!isOwn && (
        <XStack className="items-center gap-2">
          <WikiUserAvatar
            userId={author.authorId}
            name={author.authorName}
            className="size-6 text-[0.5625rem]"
          />
          <span className="text-sm font-medium text-foreground">
            {author.authorName}
          </span>
          {isChatOwner && (
            <span className="text-xs text-foreground-subtle">Owner</span>
          )}
          {member && (
            <IconButton
              size="xs"
              icon={Icons.reply}
              aria-label={`Reply to ${author.authorName}`}
              tooltip="Reply"
              onClick={reply}
            />
          )}
        </XStack>
      )}
      {message.fileIds.length > 0 && (
        <Attachments
          fileIds={message.fileIds}
          isAssistant={false}
          key={message.id}
        />
      )}
      <div
        data-allow-selection
        className={cn(
          'max-w-[80%] break-words rounded-xl px-3.5 py-1.5 text-foreground',
          isOwn ? 'bg-surface-hover' : 'bg-surface-active'
        )}
      >
        <Markdown
          id={`thread-${threadId}-team-post-${message.id}`}
          content={body}
        />
      </div>
    </div>
  );
});
